import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LabShell from "@/components/LabShell";
import usePageMeta from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COPY = {
  fan: {
    name: "Lab Listener",
    blurb: "Keeps the lights on and the servers humming. You hear about every new experiment first.",
    tint: "#FEF3D6",
    ink: "#9A6E12",
  },
  patron: {
    name: "Lab Explorer",
    blurb: "Chai. Pays for the half-built toys that don't work yet — the ones that eventually do.",
    tint: "#E3F3F5",
    ink: "#15545F",
  },
  producer: {
    name: "Lab Benefactor",
    blurb: "Underwrites a whole new experiment, start to finish, free for everyone who shows up.",
    tint: "#FFF0EA",
    ink: "#B8482A",
  },
};
const ORDER = ["fan", "patron", "producer"];

export default function SupportLab() {
  usePageMeta({
    title: "Support the lab",
    description:
      "Everything in the Jewish Music Lab is free and always will be. Chip in monthly or give once to cover the hosting and the next experiment.",
    path: "/support",
  });
  const [params] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState("");
  const [showOneTime, setShowOneTime] = useState(false);
  const [amount, setAmount] = useState("18");

  useEffect(() => {
    axios
      .get(`${API}/patronage/tiers`)
      .then(({ data }) => setTiers(data?.tiers || []))
      .catch(() => setTiers([]));
    if (params.get("checkout") === "cancelled") {
      toast.message("No worries — nothing was charged.");
    }
  }, [params]);

  const startMonthly = async (key) => {
    setBusy(key);
    try {
      const { data } = await axios.post(`${API}/patronage/checkout`, {
        tier: key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn't open checkout. Try again?");
      setBusy("");
    }
  };

  const startOneTime = async () => {
    const value = parseFloat(amount);
    if (!Number.isFinite(value) || value < 2) {
      toast.error("Pick an amount of $2 or more.");
      return;
    }
    setBusy("once");
    try {
      const { data } = await axios.post(`${API}/donate/checkout`, {
        amount: value,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open checkout.");
      setBusy("");
    }
  };

  const ordered = ORDER.map((k) => tiers.find((t) => t.key === k)).filter(Boolean);

  return (
    <LabShell>
      <Toaster position="bottom-right" richColors />

      <section className="pt-14 sm:pt-20 pb-8">
        <p className="text-xs uppercase mb-4" style={{ color: "#8C8198", fontFamily: "'Courier Prime', monospace", letterSpacing: "0.22em" }}>
          Keep the lab open
        </p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-[24ch]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
          data-testid="support-title"
        >
          Support the <span style={{ color: "#E86F4A" }}>Jewish Music Lab</span>.
        </h1>
        <p className="mt-5 text-base sm:text-lg max-w-[62ch]" style={{ color: "#5C5268", lineHeight: 1.75 }}>
          Everything in here is free and always will be. Supporters cover the hosting, the
          long nights of debugging, and the next experiment that nobody asked for yet.
        </p>
      </section>

      <section className="grid gap-5 md:grid-cols-3 pb-8">
        {ordered.map((t) => {
          const copy = TIER_COPY[t.key];
          return (
            <div
              key={t.key}
              data-testid={`support-tier-card-${t.key}`}
              className="rounded-3xl p-7 flex flex-col"
              style={{ backgroundColor: "#FFFFFF", border: "1px solid #EBE3D5" }}
            >
              <span
                className="self-start rounded-full px-3 py-1 text-[10px] uppercase mb-4"
                style={{ backgroundColor: copy.tint, color: copy.ink, fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
              >
                {copy.name}
              </span>
              <p className="text-3xl mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                ${t.amount}
                <span className="text-sm" style={{ color: "#8C8198" }}>
                  {" "}
                  / month
                </span>
              </p>
              <p className="text-sm mb-6 flex-1" style={{ color: "#5C5268", lineHeight: 1.7 }}>
                {copy.blurb}
              </p>
              <Button
                onClick={() => startMonthly(t.key)}
                disabled={!!busy}
                data-testid={`support-tier-button-${t.key}`}
                className="rounded-full h-11 bg-[#D99B26] hover:bg-[#C2891D] text-white"
                style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.14em" }}
              >
                {busy === t.key ? "Opening…" : "CHIP IN MONTHLY"}
              </Button>
            </div>
          );
        })}
      </section>

      <section className="pb-20">
        <div className="rounded-3xl p-8" style={{ backgroundColor: "#FFFBF2", border: "1px solid #EBE3D5" }}>
          {!showOneTime ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Prefer a one-time donation?
                </h2>
                <p className="text-sm" style={{ color: "#5C5268" }}>
                  Any amount, once. No subscription, no follow-up.
                </p>
              </div>
              <Button
                onClick={() => setShowOneTime(true)}
                data-testid="support-one-time-button"
                className="rounded-full h-11 px-6 bg-[#E86F4A] hover:bg-[#D35A36] text-white"
                style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.14em" }}
              >
                GIVE ANY AMOUNT
              </Button>
            </div>
          ) : (
            <div data-testid="support-one-time-panel">
              <h2 className="text-2xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                One-time donation
              </h2>
              <div className="flex flex-wrap gap-2 mb-4">
                {[5, 18, 36, 54, 180].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAmount(String(v))}
                    data-testid={`support-amount-${v}`}
                    className="rounded-full px-4 h-10 text-sm transition-colors"
                    style={{
                      border: "1px solid #D6CBB8",
                      backgroundColor: amount === String(v) ? "#FEF3D6" : "#FFFFFF",
                      color: amount === String(v) ? "#9A6E12" : "#5C5268",
                      fontFamily: "'Courier Prime', monospace",
                    }}
                  >
                    ${v}
                  </button>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 max-w-md">
                <Input
                  type="number"
                  min="2"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="support-amount-input"
                  className="h-11 bg-white border-[#D6CBB8] text-[#1A1523]"
                />
                <Button
                  onClick={startOneTime}
                  disabled={busy === "once"}
                  data-testid="support-one-time-checkout"
                  className="h-11 rounded-full px-6 bg-[#E86F4A] hover:bg-[#D35A36] text-white"
                  style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.14em" }}
                >
                  {busy === "once" ? "Opening…" : "DONATE"}
                </Button>
              </div>
              <p className="mt-3 text-xs" style={{ color: "#8C8198" }}>
                Secure checkout by Stripe. Minimum $2.
              </p>
            </div>
          )}
        </div>
      </section>
    </LabShell>
  );
}
